import { useNavigate, useParams } from "react-router-dom";
import { Page } from "../Components/page/Page";
import { useSellRequest, STATUS_COLOR } from "../lib/sell-request";
import Title from "../Components/page/Title";
import { Labelled } from "../Components";
import { dateFromPython, useClickOutside, useEscapeKey } from "../util";
import { ButtonCancel, ButtonOk } from "../Components/buttons";
import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { TreasuryClient, getActiveClient } from "../lib/client";
import { TwinPersona } from "../Components/TwinPersona";

export default function SellRequest() {
  let navigate = useNavigate();
  let { id } = useParams();
  let treasury = TreasuryClient.from(getActiveClient());
  let { data } = useSellRequest(id);

  if (!data) return <Page></Page>

  function MarkAsPaidForm() {
    let [isEditing, setIsEditing] = useState(false);
    let formRef = useRef();
    useEscapeKey(() => setIsEditing(false));;
    useClickOutside(() => setIsEditing(false), formRef)

    let { mutate: markPaid } = useMutation({
      mutationFn: async function markPaidFn({id, payoutInfo}) {
        return await treasury.addPayoutInfo(id, payoutInfo);
      },
      onSuccess: function updateStatus() {
        navigate(0); // refresh page
      },
      onError: function printError(e) {
        console.warn(e)
      }
    });

    function onSubmitForm(e) {
      e.preventDefault();
      let formData = new FormData(e.target);
      let content = formData.get("payout-info-content")
      // let attachment = formData.get("attachment") // TODO: maybe in the future
      markPaid({ id, payoutInfo: { content /*, attachment */ }})
    }

    return <>{!isEditing ?
      <ButtonOk className="max-w-fit" onClick={() => setIsEditing(true)}>
        Mark as PAID
      </ButtonOk>
      :
      <form ref={formRef} onSubmit={onSubmitForm} className="flex flex-col gap-1">
        <textarea
          name="payout-info-content"
          className="border rounded py-2 px-3 min-h-32"
          required
          placeholder="Paste payout info here"/>
        <div className="w-full flex flex-row justify-between">
          <ButtonCancel type="button" className="align-bottom" onClick={()=>setIsEditing(false)}>Cancel</ButtonCancel>
          <ButtonOk type="submit" className="align-bottom" >Submit</ButtonOk>
        </div>
      </form>
    }</>
  }

  function AddCommentForm() {
    let [isEditing, setIsEditing] = useState(false);
    let formRef = useRef();
    useEscapeKey(() => setIsEditing(false));;
    useClickOutside(() => setIsEditing(false), formRef)

    let { mutate: addComment } = useMutation({
      mutationFn: async function addCommentFn({id, comment}) {
        return await treasury.addComment(id, comment)
      },
      onSuccess: async function updateComments() {
        navigate(0); //refresh page
      }
    });

    function onSubmitForm(e) {
      e.preventDefault();
      let formData = new FormData(e.target);
      let comment = formData.get("new-comment")
      console.log(comment)
      addComment({id, comment})
    }

    return <>{!isEditing ?
      <ButtonOk className="w-fit" onClick={() => setIsEditing(true)}>
        Add comment
      </ButtonOk>
      :
      <form ref={formRef} onSubmit={onSubmitForm} className="flex flex-col gap-1">
        <textarea
          name="new-comment"
          className="border rounded py-2 px-3 min-w-96 min-h-32"
          required
          placeholder="Write a comment"/>
        <div className="flex flex-row justify-between">
          <ButtonCancel className="align-bottom" onClick={()=>setIsEditing(false)}>Cancel</ButtonCancel>
          <ButtonOk type="submit" className="align-bottom" >Submit</ButtonOk>
        </div>
      </form>
    }</>
  }

  return <Page>
    <div className="justify-center flex flex-row">
      <div className="flex flex-col md:w-1/3">
        <Title>SELL REQUEST (<span className="lowercase" title={id}>{id.slice(0,8)}</span>)</Title>
        <div className="flex flex-col gap-4">
          {(data.status.toLowerCase()!="invalid" && data.status.toLowerCase()!="paid") && <MarkAsPaidForm />}
          <Labelled label="Created">{dateFromPython(data.createdAt)}</Labelled>
          <Labelled label="Updated">{dateFromPython(data.updatedAt)}</Labelled>
          <Labelled label="Seller">
            <a href={`/profile/${encodeURIComponent(data.seller)}`}
               className="break-all" >
              <TwinPersona twinUrl={data.seller} />
            </a>
          </Labelled>
          <Labelled label="Sell value">{data.sellValue} {treasury.tdnUnit}</Labelled>
          <Labelled label="Received value">
            {data.receivedValue} {treasury.tdnUnit} ({data?.receivedDq?.length || 0} files)
            {data.receivedDq && <ul className="ml-2">
              {data.receivedDq.map(h => <li key={h}>{h.slice(0,8)}</li>)}
            </ul>}
          </Labelled>
          <Labelled label="Expected payout">{`${data.expectedPayout} ${data.expectedPayoutUnits}`}</Labelled>
          <Labelled label="Status">
            <span className={STATUS_COLOR[data.status]}>{data.status.toUpperCase()}</span>
          </Labelled>
          {data.status.toLowerCase()=="invalid" && <Labelled label="Reason">
            {data.statusReason || "N/A"}
          </Labelled>}
          {data.payoutInfo && <Labelled label="Payout info">
            {data.payoutInfo.content}
          </Labelled>}
          <Labelled label={`Comments (${data.comments.length})`}>
            {data.comments && <ul className="flex flex-col gap-2 mb-2">
              {data.comments.map(c =><li className="pl-2" key={c.id}>
                <p className="text-sm">{dateFromPython(c.createdAt)}</p>
                <pre className="ml-2 font-sans">{c.comment}</pre>
              </li>)}
            </ul>}
            <AddCommentForm />
          </Labelled>
        </div>
      </div>
    </div>
  </Page>
}
